import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, type SvgIconProps, type SxProps, type Theme, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { type ComponentType, type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { useIsMobile } from "@/hooks/useMobile";
import { ALPHA, BADGE, INFO_ICON, INK } from "@/theme/tokens";

type Props = {
	/** Shows the glyph but never its text; with `ariaLabel`, the button is disabled too (out of the Tab order, no click). */
	disabled?: boolean;
	mobileTooltipDurationMs?: number;
	title: ReactNode;
	iconSx?: SxProps;
	/**
	 * Override the default "ⓘ" glyph with a domain-specific icon (any MUI SvgIcon component) so the
	 * indicator reads as its meaning (e.g. a DST/timelapse marker) while keeping one tooltip primitive.
	 */
	icon?: ComponentType<SvgIconProps>;
	/** Overrides the default `info-icon-tooltip` test id (useful when several are rendered on one row). */
	dataTestId?: string;
	/**
	 * Already translated: makes the glyph a real `<button>` named by this ("About Copy from"), reachable by Tab, with its
	 * text shown on keyboard focus (and on a tap, on a phone) and read as the button's description. For a hint whose
	 * text the user needs but can't hover for. Absent, the glyph is a plain icon, exactly as before.
	 */
	ariaLabel?: string;
};

/** The glyph as a button: no button chrome, the hint's muted ink, and a focus ring that never moves its neighbours. */
const buttonSx = (theme: Theme): SystemStyleObject<Theme> => ({
	display: "inline-flex",
	flexShrink: 0,
	p: 0,
	m: 0,
	border: 0,
	background: "none",
	font: "inherit",
	color: INK.MUTED,
	cursor: "help",
	borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
	"&:focus-visible": { outline: "none", boxShadow: `0 0 0 ${INFO_ICON.FOCUS_RING_PX}px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
	"&:disabled": { cursor: "default" },
});

/**
 * This component renders an information icon with an associated tooltip.
 * For mobile devices, it displays a simple info icon, when clicked shows the tooltip for `mobileTooltipDurationMs` seconds.
 * @constructor
 */
export const InfoIconWithTooltip: FC<Props> = ({
	mobileTooltipDurationMs = 2000,
	disabled = false,
	title,
	iconSx,
	icon: IconComponent = InfoOutlined,
	dataTestId = "info-icon-tooltip",
	ariaLabel,
}) => {
	const [open, setOpen] = useState(false);
	const timeoutRef = useRef<number | null>(null);
	const { isMobile } = useIsMobile();

	const handleClick = () => {
		if (!isMobile || disabled) return;

		setOpen(true);

		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = window.setTimeout(() => {
			setOpen(false);
		}, mobileTooltipDurationMs);
	};

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const icon = (
		<IconComponent
			sx={{
				fontSize: "18px",
				cursor: isMobile ? "pointer" : undefined,
				...iconSx,
			}}
		/>
	);

	const plainTrigger = isMobile ? <IconButton onClick={handleClick}>{icon}</IconButton> : icon;
	return (
		<Tooltip
			data-testid={dataTestId}
			title={title}
			// Disabled: held shut, so no hover, focus or tap opens it. A phone opens it on a tap, for a while.
			open={disabled ? false : isMobile ? open : undefined}
			disableHoverListener={isMobile || disabled}
			disableFocusListener={isMobile}
			disableTouchListener={isMobile}
			describeChild={ariaLabel !== undefined}
		>
			{ariaLabel === undefined ? (
				plainTrigger
			) : (
				<Box component="button" type="button" aria-label={ariaLabel} disabled={disabled} onClick={handleClick} sx={buttonSx}>
					{icon}
				</Box>
			)}
		</Tooltip>
	);
};
